import React, { useEffect, useMemo, useState } from "react";
import FormSection from "../components/FormSection";
import "../styles/utilities.css";
import { listUnitsByProject } from "../services/unitsClient";
import scheduleRows from "../data/productionDailySchedule.json";
import aria169Rows from "../data/productionDailyScheduleAria169.json";
import edes120Rows from "../data/productionDailyScheduleEdes120.json";
import { fetchChecklistCatalog, fetchChecklistRun, fetchHolidaysByProject } from "../services/dailyChecklistClient";
import { normalizeUnit } from "../utils/normalizeUnit";
import {
  DEFAULT_TOTAL_WORKDAYS,
  getChecklistCatalogId,
  getChecklistTotalWorkdays,
  isAriaFourStory,
  isAriaProject,
  normalizeChecklistProjectId,
  resolveChecklistRows,
} from "../utils/checklistSchedule";

function isProjectedCloseTask(row) {
  const label = String(row?.operation ?? "").trim().toLowerCase();
  if (label === "projected close date") return true;
  const taskId = String(row?.task ?? row?.task_id ?? "");
  return taskId === "116";
}

const MILESTONE_MAP = [
  { key: "trench_foundation", label: "Trench Foundation", taskId: "2" },
  { key: "ground_plumbing", label: "Ground Plumbing", taskId: "4" },
  { key: "pour_foundation_slab", label: "Pour Foundation Slab", taskId: "14" },
  { key: "plumb_line_first_floor", label: "Plumb & Line First Floor", taskId: "24" },
  {
    key: "plumb_line_second_floor",
    label: "Plumb & Line Second Floor",
    taskId: "33",
    operationMatches: ["Plumb & Line Second Floor", "Plumb & Line Floor 2"]
  },
  {
    key: "plumb_line_third_floor",
    label: "Plumb & Line Third Floor",
    taskId: "42",
    operationMatches: ["Plumb & Line Third Floor", "Plumb & Line Third Floor Walls", "Plumb & Line Floor 3"],
    naForNonFourStoryAria: true
  },
  { key: "deliver_roof_trusses", label: "Deliver Roof Trusses", taskId: "37", operationMatches: ["Deliver Roof Trusses"] },
  { key: "sheathing_inspection", label: "Sheathing Inspection", taskId: "48", operationMatches: ["Sheathing Inspection"] },
  { key: "set_window_door_frames", label: "Set Window & Door Frames", taskId: "54", operationMatches: ["Set Window & Door Frames"] },
  {
    key: "finish_framing_lath_inspection",
    label: "Finish Framing (and Lath) Inspection",
    taskId: "67",
    operationMatches: [
      "Finish Framing (and Lath) Inspection",
      "Finish Framing & Lath Inspection",
      "Framing & Finish Lath Inspection"
    ]
  },
  { key: "drywall_nail_inspection", label: "Drywall Nail Inspection", taskId: "71", operationMatches: ["Drywall Nail Inspection"] },
  { key: "texture_drywall", label: "Texture Drywall", taskId: "77", operationMatches: ["Texture Drywall"] },
  { key: "cabinet_installation", label: "Cabinet Installation", taskId: "84", operationMatches: ["Cabinet Installation"] },
  { key: "set_appliances", label: "Set Appliances", taskId: "107", operationMatches: ["Set Appliances"] },
  { key: "presettlement_walk", label: "Presettlement Walk", taskId: "115" }
];

const PROJECT_OPTIONS = ["Aria", "Edes Court", "Fusion", "SoMi Towns", "SoMi A", "SoMi B", "Vida", "Vida 2"];

const PROJECT_CANONICAL_MAP = {
  "Fusion": "Fusion",
  "Fusion - Hayward": "Fusion",
  "Fusion Hayward": "Fusion",
  "Fusion - Live-Work": "Fusion",
  "Edes": "Edes Court",
  "Edes Court": "Edes Court",
  "SoMi Haypark": "SoMi Towns",
  "SoMi HayPark": "SoMi Towns",
  "SoMi Towns": "SoMi Towns",
  "SoMi HayView": "SoMi A",
  "SoMi Hayview": "SoMi A",
  "SoMi Building A": "SoMi A",
  "SoMi A": "SoMi A",
  "SoMi Building B": "SoMi B",
  "SoMi Haypark Condos": "SoMi B",
  "SoMi B": "SoMi B"
};

function getCanonicalProjectId(value) {
  if (!value) return "";
  return PROJECT_CANONICAL_MAP[value] || normalizeChecklistProjectId(value);
}

function getProjectAliasCandidates(value) {
  const canonical = getCanonicalProjectId(value);
  if (!canonical) return [];
  const candidates = new Set([canonical]);
  for (const [alias, target] of Object.entries(PROJECT_CANONICAL_MAP)) {
    if (target === canonical) {
      candidates.add(alias);
    }
  }
  return Array.from(candidates);
}

function toISO(d) {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function addWorkdays(startISO, days, holidays = new Set()) {
  if (!startISO || Number(days) === 0) return startISO || "";
  let d = new Date(startISO + "T00:00:00Z");
  let remaining = Number(days);
  const forward = remaining >= 0;
  if (!forward) remaining = Math.abs(remaining);

  let safety = 0;
  while (remaining > 0 && safety < 2000) {
    safety += 1;
    d.setUTCDate(d.getUTCDate() + (forward ? 1 : -1));

    const dayOfWeek = d.getUTCDay();
    const iso = toISO(d);
    const isHoliday = holidays.has(iso);

    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      if (!isHoliday) {
        remaining -= 1;
      }
    }
  }
  return toISO(d);
}

function normalizeOperation(value) {
  return String(value || "").trim().toLowerCase().replace(/\s+/g, " ");
}

function formatTaskNumber(value) {
  const parsed = Number.parseFloat(value);
  if (Number.isNaN(parsed)) return value ? String(value) : "";
  if (Number.isInteger(parsed)) return String(parsed);
  return parsed.toFixed(2);
}

function compareNumericStrings(a, b) {
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
}

function taskSortKey(value) {
  const raw = value == null ? "" : String(value).trim();
  if (!raw) return { isNumeric: false, number: null, text: "" };
  const cleaned = raw.replace(/,/g, "");
  const parsed = Number.parseFloat(cleaned);
  if (!Number.isNaN(parsed)) {
    return { isNumeric: true, number: parsed, text: raw };
  }
  return { isNumeric: false, number: null, text: raw };
}

function compareTaskIds(aId, bId) {
  const aKey = taskSortKey(aId);
  const bKey = taskSortKey(bId);

  if (aKey.isNumeric && bKey.isNumeric && aKey.number !== bKey.number) {
    return aKey.number - bKey.number;
  }
  if (aKey.isNumeric !== bKey.isNumeric) {
    return aKey.isNumeric ? -1 : 1;
  }
  return compareNumericStrings(aKey.text, bKey.text);
}

function taskOrderValue(row) {
  if (!row) return null;
  const direct = row.task_order ?? row.taskOrder;
  if (direct !== undefined && direct !== null && direct !== "") {
    const parsed = Number.parseFloat(String(direct).trim());
    if (!Number.isNaN(parsed)) return parsed;
  }
  const fallback = row.task ?? row.task_id ?? "";
  const parsed = Number.parseFloat(String(fallback).trim());
  return Number.isNaN(parsed) ? null : parsed;
}

export default function BuildingMilestones() {
  const [projectId, setProjectId] = useState("Aria");
  const [resolvedProjectId, setResolvedProjectId] = useState("");
  const [units, setUnits] = useState([]);
  const [selectedBuilding, setSelectedBuilding] = useState("");
  const [selectedUnit, setSelectedUnit] = useState("");

  const [catalogId, setCatalogId] = useState("default");
  const [catalogRows, setCatalogRows] = useState(() => (
    [...scheduleRows].sort((a, b) => compareTaskIds(a.task ?? a.task_id ?? "", b.task ?? b.task_id ?? ""))
  ));
  const [loadingCatalog, setLoadingCatalog] = useState(false);

  const [runDate, setRunDate] = useState(toISO(new Date()));
  const [projectedCloseDate, setProjectedCloseDate] = useState("");
  const [overrides, setOverrides] = useState({});
  const [completedDates, setCompletedDates] = useState({});
  const [runTotalWorkdays, setRunTotalWorkdays] = useState(null);
  const [loadingRun, setLoadingRun] = useState(false);
  const [runError, setRunError] = useState("");

  const [holidayDates, setHolidayDates] = useState([]);
  const holidaysSet = useMemo(() => new Set(holidayDates || []), [holidayDates]);

  const activeProjectId = useMemo(() => getCanonicalProjectId(projectId), [projectId]);
  const readonlyFieldStyle = useMemo(() => ({
    padding: "0.45rem 0.6rem",
    minHeight: 36,
    display: "flex",
    alignItems: "center",
    backgroundColor: "#f3f4f6",
    border: "1px solid #d1d5db",
    borderRadius: 6,
    color: "#111827"
  }), []);

  useEffect(() => {
    setResolvedProjectId("");
    setSelectedBuilding("");
    setSelectedUnit("");
  }, [projectId]);

  function prioritizeCandidates(list) {
    if (!resolvedProjectId || !list.includes(resolvedProjectId)) return list;
    const ordered = [resolvedProjectId, ...list];
    return Array.from(new Set(ordered));
  }

  useEffect(() => {
    let abort = false;
    async function run() {
      const canonical = activeProjectId || projectId;
      if (!canonical) return;
      const candidates = prioritizeCandidates(getProjectAliasCandidates(canonical));
      let loaded = false;
      for (const candidate of candidates) {
        try {
          const rsp = await listUnitsByProject({ project_id: candidate });
          const items = (rsp.items || rsp || []).map(normalizeUnit);
          if (abort) return;
          if (items.length > 0) {
            setResolvedProjectId(candidate);
            setUnits(items);
            const firstB = items[0].building_id || "";
            setSelectedBuilding(prev => prev || firstB);
            const firstU = items.find(u => u.building_id === firstB)?.unit_number || items[0].unit_number || "";
            setSelectedUnit(prev => prev || firstU);
            loaded = true;
            break;
          }
        } catch (e) {
          console.error("units load error", candidate, e);
        }
      }
      if (!loaded && !abort) {
        setUnits([]);
      }
    }
    run();
    return () => { abort = true; };
  }, [activeProjectId, projectId]);

  const buildings = useMemo(
    () => Array.from(new Set(units.map(u => u.building_id).filter(Boolean))).sort(),
    [units]
  );

  const unitsInBuilding = useMemo(
    () => units
      .filter(u => u.building_id === selectedBuilding)
      .map(u => u.unit_number)
      .filter(Boolean)
      .sort((a, b) => compareNumericStrings(a ?? "", b ?? "")),
    [units, selectedBuilding]
  );

  useEffect(() => {
    if (!selectedBuilding) {
      setSelectedUnit("");
      return;
    }
    const availableUnits = units.filter(u => u.building_id === selectedBuilding).map(u => u.unit_number).filter(Boolean);
    if (availableUnits.length === 0) {
      setSelectedUnit("");
      return;
    }
    if (!availableUnits.includes(selectedUnit)) {
      setSelectedUnit(availableUnits[0]);
    }
  }, [selectedBuilding, units, selectedUnit]);

  const selectedUnitMeta = useMemo(
    () => units.find(unit => String(unit.unit_number ?? "") === String(selectedUnit ?? "")),
    [units, selectedUnit]
  );

  const buildingNumber = selectedUnitMeta?.building_id || selectedBuilding || "";

  const totalWorkdays = useMemo(() => {
    return getChecklistTotalWorkdays({
      projectId: activeProjectId || projectId,
      buildingNumber,
      uniqueness: selectedUnitMeta?.uniqueness,
    });
  }, [selectedUnitMeta, buildingNumber, activeProjectId, projectId]);

  useEffect(() => {
    const targetCatalog = getChecklistCatalogId({
      projectId: activeProjectId || projectId,
      buildingNumber,
    });
    if (catalogId !== targetCatalog) {
      setCatalogId(targetCatalog);
    }
  }, [buildingNumber, catalogId, activeProjectId, projectId]);

  useEffect(() => {
    let abort = false;
    async function run() {
      setLoadingCatalog(true);
      try {
        const res = await fetchChecklistCatalog({ catalog_id: catalogId });
        if (abort) return;
        setCatalogRows(resolveChecklistRows({
          projectId: activeProjectId || projectId,
          buildingNumber,
          catalogRows: res.items,
          defaultRows: scheduleRows,
          aria169Rows,
          edes120Rows,
        }));
      } catch (err) {
        console.warn("catalog load error", err);
        if (!abort) {
          setCatalogRows(resolveChecklistRows({
            projectId: activeProjectId || projectId,
            buildingNumber,
            catalogRows: [],
            defaultRows: scheduleRows,
            aria169Rows,
            edes120Rows,
          }));
        }
      } finally {
        if (!abort) setLoadingCatalog(false);
      }
    }
    run();
    return () => { abort = true; };
  }, [catalogId, buildingNumber, activeProjectId, projectId]);

  useEffect(() => {
    let abort = false;
    async function run() {
      const canonical = activeProjectId || projectId;
      const projectKey = resolvedProjectId || canonical;
      if (!projectKey) return;
      try {
        const res = await fetchHolidaysByProject({ project_id: projectKey, region: selectedUnitMeta?.region });
        if (abort) return;
        const items = res.items || [];
        const dates = items.map(item => item.date).filter(Boolean);
        if (!abort) setHolidayDates(dates);
      } catch (err) {
        if (!abort) setHolidayDates([]);
      }
    }
    run();
    return () => { abort = true; };
  }, [activeProjectId, projectId, resolvedProjectId, selectedUnitMeta?.region]);

  useEffect(() => {
    let abort = false;
    async function run() {
      const canonical = activeProjectId || projectId;
      const projectKey = resolvedProjectId || canonical;
      if (!projectKey || !selectedUnit || !runDate) return;
      setLoadingRun(true);
      setRunError("");
      try {
        const data = await fetchChecklistRun({
          project_id: projectKey,
          unit_number: selectedUnit,
          run_date: runDate,
        });
        if (abort) return;
        let resolved = data;
        if (!data || Object.keys(data).length === 0) {
          const list = await fetchChecklistRun({
            project_id: projectKey,
            unit_number: selectedUnit,
          });
          if (!abort && Array.isArray(list.items) && list.items.length > 0) {
            resolved = [...list.items]
              .sort((a, b) => String(b.run_date || "").localeCompare(String(a.run_date || "")))[0];
            if (resolved?.run_date && resolved.run_date !== runDate) {
              setRunDate(resolved.run_date);
            }
          }
        } else if (data?._loaded_from_latest && data.run_date && data.run_date !== runDate) {
          setRunDate(data.run_date);
        }
        setOverrides(resolved?.overrides || {});
        setCompletedDates(resolved?.completed_dates || {});
        setProjectedCloseDate(resolved?.projected_close_date || "");
        setRunTotalWorkdays(resolved?.total_workdays ?? null);
      } catch (err) {
        if (!abort) {
          setRunError(err?.message || String(err));
          setOverrides({});
          setCompletedDates({});
          setProjectedCloseDate("");
          setRunTotalWorkdays(null);
        }
      } finally {
        if (!abort) setLoadingRun(false);
      }
    }
    run();
    return () => { abort = true; };
  }, [activeProjectId, projectId, resolvedProjectId, selectedUnit, runDate]);

  const sortedCatalogRows = useMemo(() => {
    return [...catalogRows].sort((a, b) => {
      const aOrder = taskOrderValue(a);
      const bOrder = taskOrderValue(b);
      if (aOrder !== null && bOrder !== null && aOrder !== bOrder) return aOrder - bOrder;
      if (aOrder !== null && bOrder === null) return -1;
      if (aOrder === null && bOrder !== null) return 1;
      const aId = a.task ?? a.task_id ?? "";
      const bId = b.task ?? b.task_id ?? "";
      const taskOrder = compareTaskIds(aId, bId);
      if (taskOrder !== 0) return taskOrder;
      return compareNumericStrings(a.operation ?? "", b.operation ?? "");
    });
  }, [catalogRows]);

  const catalogByTaskId = useMemo(() => {
    const map = {};
    sortedCatalogRows.forEach(row => {
      const taskId = row.task ?? row.task_id ?? "";
      if (taskId !== "") {
        map[String(taskId)] = row;
      }
    });
    return map;
  }, [sortedCatalogRows]);

  const catalogByOperation = useMemo(() => {
    const map = new Map();
    sortedCatalogRows.forEach(row => {
      const key = normalizeOperation(row.operation);
      if (key && !map.has(key)) {
        map.set(key, row);
      }
    });
    return map;
  }, [sortedCatalogRows]);

  const completionDates = useMemo(() => {
    if (!projectedCloseDate) return {};
    const map = {};
    const projectKey = activeProjectId || projectId;
    const effectiveTotal = isAriaFourStory(buildingNumber, projectKey)
      ? (totalWorkdays || DEFAULT_TOTAL_WORKDAYS)
      : (Number(runTotalWorkdays) || totalWorkdays || DEFAULT_TOTAL_WORKDAYS);
    const base = addWorkdays(projectedCloseDate, -effectiveTotal, holidaysSet);

    sortedCatalogRows.forEach((row) => {
      const taskId = row.task ?? row.task_id ?? "";
      const overrideValue = Number(overrides[taskId]) || 0;
      const dayOffset = (Number(row.offset) || 0) + overrideValue;

      if (base) {
        if (isProjectedCloseTask(row) && overrideValue === 0) {
          map[taskId] = projectedCloseDate;
        } else {
          map[taskId] = addWorkdays(base, dayOffset, holidaysSet);
        }
      }
    });
    return map;
  }, [projectedCloseDate, overrides, sortedCatalogRows, holidaysSet, runTotalWorkdays, totalWorkdays]);

  const milestoneRows = useMemo(() => {
    const projectKey = activeProjectId || projectId;
    const thirdFloorNA = isAriaProject(projectKey) && !isAriaFourStory(buildingNumber, projectKey);

    return MILESTONE_MAP.map(milestone => {
      if (milestone.naForNonFourStoryAria && thirdFloorNA) {
        return {
          ...milestone,
          taskId: "N/A",
          taskLabel: "N/A",
          projected: "N/A",
          completed: "N/A"
        };
      }

      const fallbackMatches = milestone.operationMatches || [];
      let row = null;
      const explicitTaskId = String(milestone.taskId || "");
      if (explicitTaskId) {
        row = catalogByTaskId[explicitTaskId] || null;
      }
      if (row) {
        const expectedOps = new Set([milestone.label, ...fallbackMatches].map(value => normalizeOperation(value)));
        const rowOp = normalizeOperation(row.operation);
        if (expectedOps.size > 0 && rowOp && !expectedOps.has(rowOp)) {
          row = null;
        }
      }
      if (!row) {
        const candidates = fallbackMatches.length ? fallbackMatches : [milestone.label];
        for (const candidate of candidates) {
          const hit = catalogByOperation.get(normalizeOperation(candidate));
          if (hit) {
            row = hit;
            break;
          }
        }
      }
      const resolvedTaskId = String(row?.task ?? row?.task_id ?? explicitTaskId ?? "");
      return {
        ...milestone,
        taskId: resolvedTaskId,
        taskLabel: row?.operation || "",
        projected: resolvedTaskId ? (completionDates[resolvedTaskId] || "") : "",
        completed: resolvedTaskId ? (completedDates[resolvedTaskId] || "") : ""
      };
    });
  }, [catalogByTaskId, catalogByOperation, completionDates, completedDates, activeProjectId, projectId, buildingNumber]);

  const displayedTotalWorkdays = useMemo(() => {
    const projectKey = activeProjectId || projectId;
    if (isAriaFourStory(buildingNumber, projectKey)) {
      return totalWorkdays || DEFAULT_TOTAL_WORKDAYS;
    }
    return Number(runTotalWorkdays) || totalWorkdays || DEFAULT_TOTAL_WORKDAYS;
  }, [runTotalWorkdays, totalWorkdays, activeProjectId, projectId, buildingNumber]);

  return (
    <div className="page">
      <div className="page-header">
        <h2>Building Milestones</h2>
        <p>Read-only milestone snapshot derived from the Daily Checklist.</p>
      </div>

      <FormSection title="Context">
        <div className="grid-2">
          <label>
            Project ID
            <select value={projectId} onChange={e => { setProjectId(getCanonicalProjectId(e.target.value)); }}>
              {PROJECT_OPTIONS.map(p => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </label>
          <label>
            Building ID
            <select value={selectedBuilding} onChange={e => { setSelectedBuilding(e.target.value); setSelectedUnit(""); }}>
              <option value="">-- Select --</option>
              {buildings.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          </label>
          <label>
            Unit #
            <select value={selectedUnit} onChange={e => setSelectedUnit(e.target.value)}>
              <option value="">-- Select --</option>
              {unitsInBuilding.map(u => <option key={u} value={u}>{u}</option>)}
            </select>
          </label>
          <label>
            Run Date
            <div style={readonlyFieldStyle}>{runDate || ""}</div>
          </label>
          <label>
            Projected Close Date
            <div style={readonlyFieldStyle}>{projectedCloseDate || ""}</div>
          </label>
          <label>
            Total Workdays
            <div style={readonlyFieldStyle}>{displayedTotalWorkdays || ""}</div>
          </label>
        </div>
        <div style={{ marginTop: 8, fontSize: "0.85rem", color: "#666" }}>
          Catalog: {catalogId || "default"}
        </div>
      </FormSection>

      {loadingRun && (
        <div className="banner info" style={{ marginTop: 8 }}>
          Loading Daily Checklist run...
        </div>
      )}
      {runError && (
        <div className="banner warn" style={{ marginTop: 8 }}>
          {runError}
        </div>
      )}
      {!loadingRun && selectedUnit && !projectedCloseDate && (
        <div className="banner warn" style={{ marginTop: 8 }}>
          No projected close date found for the latest Daily Checklist run.
        </div>
      )}
      {loadingCatalog && (
        <div className="banner info" style={{ marginTop: 8 }}>
          Loading checklist catalog...
        </div>
      )}

      <FormSection title="Milestones (Daily Checklist)">
        <div className="table-wrap" style={{ marginTop: 12 }}>
          <table className="table">
            <thead>
              <tr>
                <th>Milestone</th>
                <th>Task #</th>
                <th>Daily Checklist Task</th>
                <th>Projected Date</th>
                <th>Completed On</th>
              </tr>
            </thead>
            <tbody>
              {milestoneRows.map(row => (
                <tr key={row.key}>
                  <td className="text-strong">{row.label}</td>
                  <td>{formatTaskNumber(row.taskId)}</td>
                  <td>{row.taskLabel || "Not in catalog"}</td>
                  <td>{row.projected || ""}</td>
                  <td>{row.completed || ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </FormSection>
    </div>
  );
}
